/* oxlint-disable no-console*/

import * as Sentry from '@sentry/react';
import axios from 'axios';
import { get } from 'lodash-es';

import { noop } from 'ui-components/default-props/noop';
import { v4 } from 'uuid';

import { buffer$, logs$ } from './buffer';

const consoleError = console.error.bind(console);
const consoleWarning = console.warn.bind(console);
const consoleInfo = console.info.bind(console);

const environment = typeof window !== 'undefined' ? get(window, 'SS_GLOBALS.environment', 'dev') : 'dev';
const isInDevelopmentMode = environment === 'dev';
const shouldLogToBrowser = isInDevelopmentMode;

type SentryCaptureContext = Parameters<typeof Sentry.captureException>[1];

const buildCaptureContext = (extra?: Record<string, unknown>, sentryContext?: SentryCaptureContext): SentryCaptureContext | undefined => {
  if (sentryContext) {
    const base = sentryContext as Record<string, unknown>;
    return { ...base, extra: { ...(base.extra as Record<string, unknown> | undefined), ...extra } };
  }
  if (extra !== undefined) return { extra };
  return undefined;
};

const isErrorLike = (value: unknown): value is { message: string; stack: string; name?: string } =>
  value instanceof Error ||
  (value !== null &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).message === 'string' &&
    typeof (value as Record<string, unknown>).stack === 'string');

const toError = (value: { message: string; stack: string; name?: string; cause?: unknown }): Error => {
  if (value instanceof Error) return value;
  const error = new Error(value.message, { cause: value.cause });
  error.stack = value.stack;
  if (value.name) error.name = value.name;
  return error;
};

class LogServiceClass {
  metadata: Record<string, unknown>;

  constructor(metadata: Record<string, unknown>) {
    this.metadata = metadata;
  }

  extendMetadata = (metadata: Record<string, unknown>) => {
    try {
      this.metadata = { ...this.metadata, ...metadata };
    } catch (e) {
      this.logError(e instanceof Error ? e : new Error(String(e)), {
        metadataExtension: metadata,
        existingMetadata: this.metadata,
      });
    }
  };

  logInfo = shouldLogToBrowser
    ? consoleInfo
    : (msg: string, context?: object) => {
        buffer$.next({
          metadata: this.metadata,
          msg,
          context,
          level: 'info',
        });
      };

  logWarning = shouldLogToBrowser
    ? consoleWarning
    : (msg: string, context?: object) => {
        buffer$.next({
          metadata: this.metadata,
          msg,
          context,
          level: 'warn',
        });
      };

  logError: (error: unknown, extra?: Record<string, unknown>, sentryContext?: SentryCaptureContext) => void = shouldLogToBrowser
    ? consoleError
    : (error: unknown, extra?: Record<string, unknown>, sentryContext?: SentryCaptureContext) => {
        const exception = isErrorLike(error) ? toError(error) : new Error(String(error));
        Sentry.captureException(exception, buildCaptureContext(extra, sentryContext));
      };
}

export const LogService = new LogServiceClass({ sessionGuid: v4() });

const MAX_LOGS_PER_REQUEST = 5;

function chunkArray<T>(array: T[], size: number): T[][] {
  if (size <= 0) return [array];
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

logs$.subscribe((arrayOfLogs) => {
  if (arrayOfLogs.length >= 500) {
    LogService.logError(new Error('log buffer overflow'), {
      bufferLength: arrayOfLogs.length,
      logSample: arrayOfLogs[arrayOfLogs.length - 1],
    });
  } else if (arrayOfLogs.length) {
    const logChunks = chunkArray(arrayOfLogs, MAX_LOGS_PER_REQUEST);
    logChunks.forEach((chunk) => {
      axios.post('', chunk, { baseURL: '/log-bulk' }).catch(noop);
    });
  }
});
